/**
 * The decisions the identity-provider screen makes, kept out of the component
 * that draws them.
 *
 * Two of them are worth stating on their own.
 *
 * **The provider's own identity provider is offered only when the deployment
 * runs one**, and its address comes out of the answer. A console that carried
 * our issuer in its own source would name the wrong one the day a deployment
 * moved it, and a console that offered the option regardless would hand
 * somebody a cluster nobody can sign in to — the server refuses the choice at
 * the edge with `provider_identity_unavailable`, and finding that out after
 * pressing the button is not an offer, it is a trap.
 *
 * **Checking the tenancy's own coordinates before sending them** saves a round
 * trip and nothing else. Every rule here is the server's, the server applies
 * them again, and where the two disagree the server wins.
 */

import type { SimpleKey } from '../../i18n/types';
import type { IdentityChoice, SigningAlg, TenancyIdentity } from '../../api/types';

/**
 * Whether the option can be offered, and what to say when it cannot.
 *
 * `unnamed` is not a theoretical case being tidy about: the console must not
 * describe a provider it cannot name, and naming one from its own source is the
 * thing this whole shape exists to prevent.
 */
export type ProviderOption =
  | { available: true; issuer: string }
  | { available: false; reason: SimpleKey };

export function providerOption(status: TenancyIdentity): ProviderOption {
  if (!status.provider_available) {
    return { available: false, reason: 'identityProvider.provider.unavailable' };
  }
  const issuer = status.provider_issuer;
  if (issuer === undefined || issuer === '') {
    return { available: false, reason: 'identityProvider.provider.unnamed' };
  }
  return { available: true, issuer };
}

/** The state of a change, as a sentence. An unknown state is said to be unknown. */
export function stateKey(state: string): SimpleKey {
  switch (state) {
    case 'pending':
      return 'identityState.pending';
    case 'applying':
      return 'identityState.applying';
    case 'applied':
      return 'identityState.applied';
    case 'rejected':
      return 'identityState.rejected';
    case 'failed':
      return 'identityState.failed';
    default:
      return 'identityState.unknown';
  }
}

/** Why the last attempt did not take. A code this console does not know reads as `unknown`. */
export function failureKey(failure: string | undefined): SimpleKey {
  switch (failure) {
    case 'issuer_unreachable':
      return 'identityFailure.issuerUnreachable';
    case 'issuer_certificate':
      return 'identityFailure.issuerCertificate';
    case 'issuer_mismatch':
      return 'identityFailure.issuerMismatch';
    case 'algorithm_unsupported':
      return 'identityFailure.algorithmUnsupported';
    case 'signing_keys_unusable':
      return 'identityFailure.signingKeysUnusable';
    case 'no_login_flow':
      return 'identityFailure.noLoginFlow';
    case 'client_unknown':
      return 'identityFailure.clientUnknown';
    case 'cluster_unreachable':
      return 'identityFailure.clusterUnreachable';
    default:
      return 'identityFailure.unknown';
  }
}

/** Whether the last attempt stands refused or unfinished — the two a person can dismiss. */
export function isFailureState(state: string): boolean {
  return state === 'rejected' || state === 'failed';
}

/** Whether the cluster is running something other than what was last asked for. */
export function desiredDiffersFromApplied(status: TenancyIdentity): boolean {
  const applied = status.applied;
  if (applied === undefined) return true;
  return !sameChoice(applied, status.desired);
}

export function sameChoice(a: IdentityChoice, b: IdentityChoice): boolean {
  return (
    a.kind === b.kind &&
    a.issuer === b.issuer &&
    a.client_id === b.client_id &&
    a.audience === b.audience &&
    a.signing_algs.length === b.signing_algs.length &&
    a.signing_algs.every((alg, at) => alg === b.signing_algs[at])
  );
}

/** The form, before it becomes a choice. Field names are the wire's, so a
 *  server complaint about `client_id` highlights the box called `client_id`. */
export interface OwnProviderForm {
  issuer: string;
  client_id: string;
  audience: string;
  signing_algs: SigningAlg[];
}

export type OwnProviderField = 'issuer' | 'client_id' | 'audience';
export type OwnProviderErrors = Partial<Record<OwnProviderField, SimpleKey>>;

/**
 * The checks the server makes before it asks the provider anything.
 *
 * The issuer is compared byte for byte with what the provider advertises and
 * with the `iss` of every token, so the shapes that differ only in punctuation
 * are refused here rather than discovered when Auth will not start.
 */
export function checkOwnProvider(form: OwnProviderForm): OwnProviderErrors {
  const errors: OwnProviderErrors = {};
  const issuer = form.issuer.trim();
  const clientId = form.client_id.trim();
  const audience = form.audience.trim();

  if (issuer === '') errors.issuer = 'identityProvider.check.issuerRequired';
  else if (!issuer.startsWith('https://')) errors.issuer = 'identityProvider.check.issuerScheme';
  else if (issuer === 'https://') errors.issuer = 'identityProvider.check.issuerNoHost';
  else if (issuer.includes('?') || issuer.includes('#'))
    errors.issuer = 'identityProvider.check.issuerQuery';
  else if (issuer.endsWith('/')) errors.issuer = 'identityProvider.check.issuerTrailingSlash';
  else if (/\s/.test(issuer)) errors.issuer = 'identityProvider.check.issuerWhitespace';

  if (clientId === '') errors.client_id = 'identityProvider.check.clientIdRequired';
  else if (/\s/.test(clientId)) errors.client_id = 'identityProvider.check.clientIdWhitespace';

  if (/\s/.test(audience)) errors.audience = 'identityProvider.check.audienceWhitespace';

  return errors;
}

/** The form as the server takes it. Trimmed, because the server trims too. */
export function ownChoice(form: OwnProviderForm): IdentityChoice {
  return {
    kind: 'own',
    issuer: form.issuer.trim(),
    client_id: form.client_id.trim(),
    audience: form.audience.trim(),
    signing_algs: [...form.signing_algs],
  };
}

/** What the form starts as: whatever the cluster runs, or an empty form. */
export function formFor(choice: IdentityChoice | undefined): OwnProviderForm {
  if (choice === undefined || choice.kind !== 'own') {
    return { issuer: '', client_id: '', audience: '', signing_algs: [] };
  }
  return {
    issuer: choice.issuer,
    client_id: choice.client_id,
    audience: choice.audience,
    signing_algs: [...choice.signing_algs],
  };
}
